import { ChevronRightIcon } from '@kijiji/icons/src/icons'
import AppleLogo from '@kijiji/icons/src/icons/AppleLogo'
import GooglePlayLogo from '@kijiji/icons/src/icons/GooglePlayLogo'
import { breakpoints } from '@kijiji/theme'
import Image from 'next/image'
import { useTranslation } from 'next-i18next'
import { useQRCode } from 'next-qrcode'
import { useTheme } from 'styled-components'

import {
  CanvasBottomBodyText,
  CanvasBottomWrapper,
  CanvasTitle,
  CanvasWrapper,
  FooterAppBannerContainer,
  QRCodeContainer,
  QRCodeSection,
  QRTitleContainer,
} from '@/components/shared/global-footer/footer-app-banner/styled'
import { LinkCustom } from '@/components/shared/link-custom/LinkCustom'
import { TRANSLATION_KEYS } from '@/constants/localization'
import { ROUTE_LOCALE } from '@/domain/locale'
import { useLocale } from '@/hooks/useLocale'
import { trackEvent } from '@/lib/ga'
import { GA_EVENT } from '@/lib/ga/constants/gaEvent'
import { BodyText } from '@/ui/atoms/body-text'
import { Button } from '@/ui/atoms/button'
import { HeadlineText } from '@/ui/atoms/headline-text'
import { getWindowWidthBreakpointKey } from '@/ui/helpers/getWindowWidthBreakpointKey'
import { getUserDevice } from '@/utils/getUserDevice'

const FooterAppBanner = () => {
  const { colors } = useTheme()
  const { Canvas } = useQRCode()
  const { t } = useTranslation([TRANSLATION_KEYS.COMMON, TRANSLATION_KEYS.ROUTES])

  const { isPhoneOrTablet, isAppleDevice } = getUserDevice()
  const currentBreakpointKey = getWindowWidthBreakpointKey()

  const isSmallScreen = isPhoneOrTablet && breakpoints[currentBreakpointKey] <= breakpoints.large
  const handleLinkClick = () => {
    trackEvent({ action: GA_EVENT.DownloadAppClick })
  }
  const { routeLocale } = useLocale()

  const isFrench = routeLocale === ROUTE_LOCALE.fr

  return (
    <FooterAppBannerContainer>
      <Image
        alt={t('footer_app_banner.image.label')}
        width={isSmallScreen ? 132 : 331}
        height={isSmallScreen ? 213 : 504}
        src={t(`${TRANSLATION_KEYS.ROUTES}:footer_app_banner.image.href`)}
      />
      <QRCodeContainer isFrench={isFrench}>
        <QRTitleContainer>
          <HeadlineText
            as="p"
            size={isSmallScreen ? 'large' : 'giant'}
            color={colors.purple.primary}
          >
            {t('footer_app_banner.get_kijiji_app')}
          </HeadlineText>
          <BodyText size={isSmallScreen ? 'small' : 'xLarge'} color={colors.purple.primary}>
            {t('footer_app_banner.personalized_easy')}
          </BodyText>
        </QRTitleContainer>

        {isSmallScreen ? (
          <LinkCustom
            aria-label={t(`footer_app_banner.${isAppleDevice ? 'app_store' : 'google_play'}.label`)}
            href={t(
              `${TRANSLATION_KEYS.ROUTES}:external.${isAppleDevice ? 'app_store' : 'google_play'}.href`
            )}
            onClick={handleLinkClick}
            rel="noopener noreferrer"
            target="_blank"
          >
            <Button variant="secondary">{t('footer_app_banner.use_the_app')}</Button>
          </LinkCustom>
        ) : (
          <QRCodeSection>
            <CanvasWrapper isMobile={isPhoneOrTablet}>
              <CanvasTitle>
                <AppleLogo size="xsm" />
                {t('footer_app_banner.scan_to_ios')}
              </CanvasTitle>
              <Canvas
                text={t(`${TRANSLATION_KEYS.ROUTES}:external.app_store.href`)}
                options={{
                  level: 'M',
                  margin: 0,
                  scale: 5,
                  width: 130,
                  color: {
                    dark: colors.purple.light4,
                    light: colors.grey.primary,
                  },
                }}
              />
              {isPhoneOrTablet && (
                <LinkCustom
                  aria-label={t('footer_app_banner.app_store.label')}
                  href={t(`${TRANSLATION_KEYS.ROUTES}:external.app_store.href`)}
                  onClick={handleLinkClick}
                  rel="noopener noreferrer"
                  target="_blank"
                  noStyle
                >
                  <CanvasBottomWrapper>
                    <CanvasBottomBodyText size="xLarge">
                      {t('footer_app_banner.see_on_app_store')}
                    </CanvasBottomBodyText>
                    <ChevronRightIcon color={colors.white} />
                  </CanvasBottomWrapper>
                </LinkCustom>
              )}
            </CanvasWrapper>

            <CanvasWrapper isMobile={isPhoneOrTablet}>
              <CanvasTitle>
                <GooglePlayLogo size="xsm" />
                {t('footer_app_banner.scan_to_android')}
              </CanvasTitle>
              <Canvas
                text={t(`${TRANSLATION_KEYS.ROUTES}:external.google_play.href`)}
                options={{
                  level: 'M',
                  margin: 0,
                  scale: 5,
                  width: 130,
                  color: {
                    dark: colors.purple.light4,
                    light: colors.grey.primary,
                  },
                }}
              />
              {isPhoneOrTablet && (
                <LinkCustom
                  aria-label={t('footer_app_banner.google_play.label')}
                  href={t(`${TRANSLATION_KEYS.ROUTES}:external.google_play.href`)}
                  onClick={handleLinkClick}
                  rel="noopener noreferrer"
                  target="_blank"
                  noStyle
                >
                  <CanvasBottomWrapper>
                    <CanvasBottomBodyText size="xLarge">
                      {t('footer_app_banner.see_on_play_store')}
                    </CanvasBottomBodyText>
                    <ChevronRightIcon color={colors.white} />
                  </CanvasBottomWrapper>
                </LinkCustom>
              )}
            </CanvasWrapper>
          </QRCodeSection>
        )}
      </QRCodeContainer>
    </FooterAppBannerContainer>
  )
}

export default FooterAppBanner
